import React, { useEffect, useState } from "react";
import { Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHead, Panel } from "@/pages/vendor/Shell";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

function AttendeeSheet({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const [d, setD] = useState<any>(null);
  const load = () => api.get(`/admin/attendees/${id}`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const suspend = async (v: boolean) => {
    try { await api.patch(`/admin/attendees/${id}`, { suspended: v }); toast.success("Attendee updated"); load(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="admin-attendee-sheet">
        <SheetHeader><SheetTitle>{d?.attendee.name || "Attendee"}</SheetTitle></SheetHeader>
        {d && (
          <div className="mt-4 space-y-5 text-sm">
            <p className="text-muted-foreground">{d.attendee.email}{d.profile?.phone ? ` · ${d.profile.phone}` : ""}</p>
            <label className="flex items-center justify-between rounded-sm border p-3">
              <span><b>Suspend attendee</b><br /><span className="text-xs text-muted-foreground">Blocks access to their saved vendors</span></span>
              <Switch checked={!!d.attendee.suspended} onCheckedChange={suspend} data-testid="admin-attendee-suspend-switch" />
            </label>
            <div>
              <p className="mb-2 font-semibold">Saved vendors</p>
              {d.leads.map((l: any) => <p key={l.id} className="text-xs">{l.vendor} · {l.event_name} · {l.stage}</p>)}
            </div>
            <div data-testid="admin-attendee-consents">
              <p className="mb-2 font-semibold">Consent records</p>
              {d.consents.map((c: any) => (
                <div key={c.id} className="mb-2 rounded-sm border p-2 text-xs">
                  <p className="flex items-center gap-1 font-semibold"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> {c.vendor_name} · {c.consent_text_version} · {new Date(c.created_at).toLocaleString()}</p>
                  <p className="text-muted-foreground">Share: yes · Marketing: {c.marketing_consent ? "yes" : "no"} · via {(c.source || "qr").toUpperCase()}{c.ip ? ` · IP ${c.ip}` : ""}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function AdminAttendees() {
  const [rows, setRows] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const load = () => api.get("/admin/attendees", { params: { q: q || undefined } }).then((r) => setRows(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <PageHead eyebrow="Customer service" title="Attendees & consent" />
      <div className="relative mb-4 max-w-md"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-attendee-search" /></div>
      <Panel className="overflow-x-auto p-0" data-testid="admin-attendees-table">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Vendors saved</th><th className="p-3">Status</th></tr></thead>
          <tbody className="divide-y">{rows.map((a) => (
            <tr key={a.id} onClick={() => setOpen(a.id)} className="cursor-pointer hover:bg-muted" data-testid={`admin-attendee-row-${a.id}`}>
              <td className="p-3 font-semibold">{a.name}</td><td className="p-3 text-xs">{a.email}</td><td className="p-3 font-mono">{a.vendors_saved}</td>
              <td className="p-3">{a.suspended ? <span className="text-rose-600">Suspended</span> : <span className="text-green-700">Active</span>}</td>
            </tr>
          ))}</tbody>
        </table>
      </Panel>
      {open && <AttendeeSheet id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
